import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Lock, Mail, HelpCircle, MessageCircle, X, Clock, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useLoginContext } from '../context/LoginContext';
import LoginLeftSide from '../components/LoginLeftSide';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { 
    transform: translateY(-20px) scale(0.95);
    opacity: 0;
  }
  to { 
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const RightSection = styled.div`
  width: 100%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin-left: 0;
  min-height: 100vh;

  @media (min-width: 1024px) {
    width: 50%;
    margin-left: 50%;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 28rem;
  padding: 2rem;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const FormTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    text-align: center;
  }
`;

const FormSubtitle = styled.p`
  color: #000;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    text-align: center;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 2px #1562B7;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 0.75rem 0.75rem 3rem;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const OptionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #4b5563;
`;

const CheckboxInput = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #1562B7;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
`;

const ForgotPasswordLink = styled.a`
  font-size: 0.875rem;
  color: #1562B7;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #0f529c;
  }
`;

const StyledButton = styled.button`
  width: 100%;
  background-color: #1562B7;
  color: #fff;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0f529c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const SignUpPrompt = styled.div`
  margin-top: 2rem;
  text-align: center;
  font-size: 0.875rem;
  color: #4b5563;

  a {
    color: #1562B7;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #422d52;
    }
  }
`;

const SupportLinks = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #6b7280;
    transition: color 0.3s;

    &:hover {
      color: #374151;
    }

    svg {
      margin-right: 0.25rem;
    }
  }
`;

// Enhanced OTP Modal Components
const OtpModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${({ show }) => (show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
  padding: 1rem;
`;

const OtpModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
    background: #e5e7eb;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const InstructionText = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;

  strong {
    color: #1f2937;
    font-weight: 600;
  }
`;

const OtpInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 0.5rem;
`;

const OtpDigitInput = styled.input`
  width: 50px;
  height: 50px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #1562B7;
    box-shadow: 0 0 0 3px rgba(21, 98, 183, 0.1);
  }

  &::placeholder {
    color: #d1d5db;
  }

  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }
`;

const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #1562B7;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-left: 0.5rem;

  &:hover {
    color: #0f529c;
    text-decoration: underline;
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  background-color: #1562B7;
  color: #fff;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0f529c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background-color: #f3f4f6;
  color: #374151;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e5e7eb;
  }

  @media (max-width: 480px) {
    order: 2;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  text-align: center;
`;

// Enhanced OTP Modal Component
const EnhancedOtpModal = ({ 
  show, 
  onClose, 
  email, 
  onVerify, 
  isVerifying, 
  errorMsg,
  onResend 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (show) {
      setOtp(['', '', '', '', '', '']);
      setTimeLeft(60);
      setCanResend(false);
      
      // Focus first input when modal opens
      if (inputRefs.current[0]) {
        setTimeout(() => inputRefs.current[0].focus(), 100);
      }
    }
  }, [show]);

  useEffect(() => {
    if (timeLeft === 0) {
      setCanResend(true);
      return;
    }

    if (!show) return;

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, show]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
    
    // Auto-submit if all digits are entered
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerify();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, 6);
    
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').slice(0, 6);
      setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
      
      // Focus the next empty input
      const nextIndex = Math.min(newOtp.length, 5);
      inputRefs.current[nextIndex].focus();
    }
  };

  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length === 6) {
      onVerify(otpCode);
    }
  };

  const handleResend = () => {
    if (canResend) {
      onResend();
      setTimeLeft(60);
      setCanResend(false);
    }
  };

  return (
    <OtpModalOverlay show={show} onClick={onClose}>
      <OtpModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Enter Verification Code</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <InstructionText>
            We've sent a 6-digit verification code to <strong>{email}</strong>. 
            Please enter it below to continue.
          </InstructionText>
          
          <OtpInputContainer>
            {otp.map((digit, index) => (
              <OtpDigitInput
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={e => handleOtpChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                placeholder="•"
                autoComplete="one-time-code"
              />
            ))}
          </OtpInputContainer>
          
          {/* <TimerContainer>
            <Clock size={16} style={{ marginRight: '0.5rem' }} />
            {canResend ? (
              <span>Didn't receive the code?</span>
            ) : (
              <span>Resend code in {timeLeft}s</span>
            )}
            <ResendButton 
              onClick={handleResend} 
              disabled={!canResend}
            >
              Resend
            </ResendButton>
          </TimerContainer> */}
          
          <ButtonGroup>
            <PrimaryButton 
              onClick={handleVerify} 
              disabled={isVerifying || otp.join('').length !== 6}
            >
              {isVerifying ? (
                <>
                  <RefreshCw size={18} className="spinner" style={{ marginRight: '0.5rem' }} />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </PrimaryButton>
            
            <SecondaryButton onClick={onClose}>
              Cancel
            </SecondaryButton>
          </ButtonGroup>
          
          {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
        </ModalBody>
      </OtpModalContent>
    </OtpModalOverlay>
  );
};

// Main Login Page Component
function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const { login } = useLoginContext();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsLoggingIn(true);

    try {
      const response = await apiRequest('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMsg(data.message || 'Login failed');
      } else {
        setIsOtpModalOpen(true);
      }
    } catch (error) {
      setErrorMsg('Login failed: ' + error.message);
    }
    setIsLoggingIn(false);
  };

  const handleVerifyOtp = async (otpCode) => {
    setErrorMsg('');
    setIsVerifyingOtp(true);

    try {
      const response = await apiRequest('/api/auth/verify-2fa', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ email, otp: otpCode }),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMsg(data.message || 'OTP verification failed');
      } else {
        login(data.token);
        navigate('/');
      }
    } catch (error) {
      setErrorMsg('Error verifying OTP: ' + error.message);
    }
    setIsVerifyingOtp(false);
  };

  const handleResendOtp = async () => {
    try {
      // Call API to resend OTP
      const response = await apiRequest('/api/auth/resend-otp', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ email }),
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        setErrorMsg('Failed to resend OTP');
      }
    } catch (error) {
      setErrorMsg('Error resending OTP: ' + error.message);
    }
  };

  return (
    <PageContainer>
      <LoginLeftSide />
      <RightSection>
        <FormContainer>
          <FormTitle>Welcome Back</FormTitle>
          <FormSubtitle>Sign in with your email and password</FormSubtitle>
          <StyledForm onSubmit={handleSubmit}>
            <FieldGroup>
              <StyledLabel htmlFor="email">Email Address</StyledLabel>
              <InputWrapper>
                <InputIcon><Mail size={20} /></InputIcon>
                <StyledInput
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </InputWrapper>
            </FieldGroup>
            <FieldGroup>
              <StyledLabel htmlFor="password">Password</StyledLabel>
              <InputWrapper>
                <InputIcon><Lock size={20} /></InputIcon>
                <StyledInput
                  type="password"
                  id="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </InputWrapper>
            </FieldGroup>
            <OptionsRow>
              <CheckboxLabel>
                <CheckboxInput
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <span style={{ marginLeft: '0.5rem' }}>Remember me</span>
              </CheckboxLabel>
              <ForgotPasswordLink href="/forgot-password">Forgot password?</ForgotPasswordLink>
            </OptionsRow>
            <StyledButton type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? 'Signing In...' : 'Sign In'}
            </StyledButton>
          </StyledForm>
          {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          <SignUpPrompt>Don't have an account? <a href="/signup">Create Account</a></SignUpPrompt>
          <SupportLinks>
            <a href="https://help.zerodayops.com"><HelpCircle size={16} /> Help</a>
            <a href="mailto:contact@zerodayops.com"><MessageCircle size={16} /> Contact Support</a>
          </SupportLinks>
        </FormContainer>
      </RightSection>

      <EnhancedOtpModal
        show={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        email={email}
        onVerify={handleVerifyOtp}
        isVerifying={isVerifyingOtp}
        errorMsg={errorMsg}
        onResend={handleResendOtp}
      />
    </PageContainer>
  );
}

export default LoginPage;